import { useState, useEffect, useCallback, useMemo } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import TableBody from '@mui/material/TableBody';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import { Alert, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';

import { useRouter } from 'src/routes/hooks';
import { useBoolean } from 'src/hooks/use-boolean';
import { useTable } from 'src/hooks/use-table';

import { getUserIdFromToken } from 'src/utils/jwt';

import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';

import { type Permission, getUserPermissions, deletePermission, getUserPermissionsAll } from 'src/api/permissionApi';

import LeaveExportModal from '../components/LeaveExportModal';

import { TableNoData } from '../../employee/table-no-data';
import { TableEmptyRows } from '../../employee/table-empty-rows';
import PermissionTableRow from '../components/list/PermissionTableRow';
import PermissionTableHead from '../components/list/PermissionTableHead';
import PermissionTableToolbar from '../components/list/PermissionTableToolbar';
import { emptyRows } from '../../employee/utils';

// ----------------------------------------------------------------------

// Permission için özel applyFilter fonksiyonu
const applyFilter = ({
  inputData,
  comparator,
  filterName,
}: {
  inputData: Permission[];
  comparator: (a: any, b: any) => number;
  filterName: string;
}) => {
  const stabilizedThis = inputData.map((el, index) => [el, index] as const);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  inputData = stabilizedThis.map((el) => el[0]);

  if (filterName) {
    inputData = inputData.filter(
      (permission) =>
        (permission.employee?.firstName?.toLowerCase() || '').indexOf(filterName.toLowerCase()) !== -1 ||
        (permission.employee?.lastName?.toLowerCase() || '').indexOf(filterName.toLowerCase()) !== -1 ||
        (permission.reason?.toLowerCase() || '').indexOf(filterName.toLowerCase()) !== -1
    );
  }

  return inputData;
};

// Permission için özel getComparator fonksiyonu
const getComparator = (order: 'asc' | 'desc', orderBy: string) => 
  order === 'desc'
    ? (a: Permission, b: Permission) => descendingComparator(a, b, orderBy)
    : (a: Permission, b: Permission) => -descendingComparator(a, b, orderBy);

const descendingComparator = (a: Permission, b: Permission, orderBy: string) => {
  const aValue = a[orderBy as keyof Permission];
  const bValue = b[orderBy as keyof Permission];
  
  if (bValue === undefined || bValue === null) return -1;
  if (aValue === undefined || aValue === null) return 1;
  
  if (bValue < aValue) {
    return -1;
  }
  if (bValue > aValue) {
    return 1;
  }
  return 0;
};

export function PermissionView() {
  const table = useTable();
  const router = useRouter();
  const confirm = useBoolean();
  const exportModal = useBoolean();
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [filterName, setFilterName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [permissionToDelete, setPermissionToDelete] = useState<Permission | null>(null);

  const fetchPermissions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const userId = getUserIdFromToken();
      if (!userId) {
        throw new Error('Kullanıcı ID bulunamadı');
      }

      const data = await getUserPermissionsAll();
      const userPermissions = data?.content as any || [];
      setPermissions(userPermissions);
    } catch (err: any) {
      console.error('Error fetching permissions:', err);
      setError(err.message || 'İzinler yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    } 
  }, []);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // Frontend filtreleme
  const filteredPermissions = useMemo(() => {
    if (!filterName.trim()) {
      return permissions;
    }
    
    return permissions.filter((permission) => 
      `${permission.employee?.firstName || ''} ${permission.employee?.lastName || ''}`.toLowerCase().includes(filterName.toLowerCase()) ||
      permission.reason?.toLowerCase().includes(filterName.toLowerCase()) ||
      permission.leaveType?.toLowerCase().includes(filterName.toLowerCase()) ||
      permission.status?.toLowerCase().includes(filterName.toLowerCase())
    );
  }, [permissions, filterName]);

  const notFound = !filteredPermissions.length && !loading && !filterName;

  const handleFilterByName = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      table.setPage(0); // 0-based indexing
      setFilterName(event.target.value);
    },
    [table]
  );

  const handleClearSearch = useCallback(() => {
    setFilterName('');
    table.setPage(0);
  }, [table]);

  const handleDeleteRow = useCallback(
    (id: string) => {
      const permission = permissions.find((perm) => perm.id === id);
      if (permission) {
        setPermissionToDelete(permission);
        confirm.onTrue();
      }
    },
    [confirm, permissions]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (permissionToDelete) {
      try {
        await deletePermission(permissionToDelete.id);
        await fetchPermissions();
        confirm.onFalse();
        setPermissionToDelete(null);
      } catch (err: any) {
        console.error('Failed to delete permission:', err);
        setError(err.message || 'İzin silinirken bir hata oluştu.');
        confirm.onFalse();
      }
    }
  }, [permissionToDelete, fetchPermissions, confirm]);

  const handleEditRow = useCallback(
    (id: string) => {
      router.push(`/dashboard/permissions/${id}`);
    },
    [router]
  );

  return (
    <>
      <Container maxWidth={false}>
        <Box display="flex" alignItems="center" mb={5}>
          <Typography variant="h4" flexGrow={1}>
            İzin Talepleri
          </Typography>
          <Button
            variant="contained"
            color="inherit"
            startIcon={<Iconify icon="mingcute:add-line" />}
            onClick={() => router.push('/dashboard/permissions/new')}
          >
            Yeni İzin Talebi
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Card>
          <PermissionTableToolbar
            numSelected={table.selected.length}
            filterName={filterName}
            onFilterName={handleFilterByName}
            onClearSearch={handleClearSearch}
            onExportClick={exportModal.onTrue}
          />

          <Scrollbar>
            {loading ? (
              <Typography variant="h6" align="center" sx={{ p: 3 }}>
                Yükleniyor...
              </Typography>
            ) : (
              <TableContainer sx={{ overflow: 'unset' }}>
                <Table sx={{ minWidth: 800 }}>
                  <PermissionTableHead
                    order={table.order}
                    orderBy={table.orderBy}
                    rowCount={filteredPermissions.length}
                    numSelected={table.selected.length}
                    onSort={table.onSort}
                    onSelectAllRows={(checked: boolean) =>
                      table.onSelectAllRows(
                        checked,
                        filteredPermissions.map((permission) => permission.id)
                      )
                    }
                  />
                  <TableBody>
                    {filteredPermissions
                      .slice(
                        table.page * table.rowsPerPage,
                        table.page * table.rowsPerPage + table.rowsPerPage
                      )
                      .map((row) => (
                        <PermissionTableRow
                          key={row.id}
                          permission={row}
                          selected={table.selected.includes(row.id)}
                          onSelectRow={() => table.onSelectRow(row.id)}
                          onDeleteSuccess={fetchPermissions}
                        />
                      ))}

                    <TableEmptyRows
                      height={68}
                      emptyRows={emptyRows(table.page, table.rowsPerPage, filteredPermissions.length)}
                    />

                    {notFound && <TableNoData searchQuery={filterName} />}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Scrollbar>

          <TablePagination
            component="div"
            page={filterName ? 0 : table.page}
            count={filterName ? filteredPermissions.length : permissions.length}
            rowsPerPage={filterName ? filteredPermissions.length : table.rowsPerPage}
            onPageChange={filterName ? () => {} : table.onChangePage}
            rowsPerPageOptions={filterName ? [] : [5, 10, 25]}
            onRowsPerPageChange={filterName ? () => {} : table.onChangeRowsPerPage}
            labelRowsPerPage="Sayfa başına satır:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} / ${count !== -1 ? count : `${to}'den fazla`}`
            }
          />
        </Card>
      </Container>

      <Dialog open={confirm.value} onClose={confirm.onFalse}>
        <DialogTitle>İzni Sil</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Bu izin talebini silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={confirm.onFalse} color="inherit">
            İptal
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      <LeaveExportModal
        open={exportModal.value}
        onClose={exportModal.onFalse}
        currentFilters={{
          // Mevcut filtreleri buraya ekleyebiliriz
          // startDate: ...,
          // endDate: ...,
          // branchId: ...,
        }}
        isSuperAdmin={false} // Bu değeri user role'üne göre belirleyebiliriz
      />
    </>
  );
} 